"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Button } from "@ui/shad"
import { ScrollArea } from "@ui/shad/scroll-area"
import { cva, VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { useDialogEntryId } from "@/components/dialogs/dialog-entry-context"

import { cn } from "../utils"
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in fixed inset-0 z-50 bg-black/40 duration-150! dark:bg-black/70",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const dialogContentVariants = cva(
  "fixed z-50 grid border bg-background shadow-lg [&>*]:min-w-0 data-[state=closed]:h-40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-75 data-[state=open]:fade-in duration-150!",
  {
    variants: {
      position: {
        default: "left-[50%] top-24 w-full max-w-(--breakpoint-md) translate-x-[-50%] sm:rounded-lg",
        pinned:
          "right-4 bottom-4 w-full max-w-(--breakpoint-md) rounded-lg data-[state=closed]:slide-out-to-bottom-4 data-[state=open]:slide-in-from-bottom-4",
      },
      scrollable: {
        true: "max-h-[calc(100vh-8rem)]",
        false: "overflow-hidden",
      },
    },
    defaultVariants: {
      position: "default",
      scrollable: false,
    },
  }
)

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {
  overlayClassName?: string
}

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, children, position, scrollable, overlayClassName, ...props }, ref) => (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(dialogContentVariants({ position, scrollable }), className)}
        onInteractOutside={position === "pinned" ? (e) => e.preventDefault() : undefined}
        {...props}
      >
        {scrollable ? <ScrollArea className="h-full max-h-[calc(100vh-8rem)]">{children}</ScrollArea> : children}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  children,
  actions = [],
  onClose,
  closeDisabled,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  closeDisabled?: boolean
  onClose?: () => void
  actions?: Array<
    | {
        key: string
        content: React.ReactNode
      }
    | {
        key: string
        label: string
        icon: React.ElementType
        onClick: () => void
        active?: boolean
      }
  >
}) => (
  <div className={cn("flex h-10 flex-row items-center justify-between space-y-0 border-b pr-2 pl-4", className)}>
    <div className="grow text-sm font-medium" {...props}>
      {children}
    </div>
    <div className="flex items-center space-x-0.5">
      {actions.length > 0 && (
        <>
          {actions.map((action) => (
            <React.Fragment key={action.key}>
              {"content" in action ? (
                action.content
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button onClick={action.onClick} variant={action.active ? "secondary" : "ghost"} size="iconXs">
                      <action.icon className="text-icon" />
                      <span className="sr-only">{action.label}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{action.label}</TooltipContent>
                </Tooltip>
              )}
            </React.Fragment>
          ))}
          <div aria-hidden className="bg-border mx-0.5 h-4 w-px" />
        </>
      )}
      {onClose ? (
        <Button type="button" variant="ghost" size="iconXs" onClick={onClose} disabled={closeDisabled}>
          <X className="text-icon" />
          <span className="sr-only">Close</span>
        </Button>
      ) : (
        <DialogClose asChild>
          <Button variant="ghost" size="iconXs" disabled={closeDisabled}>
            <X className="text-icon" />
            <span className="sr-only">Close</span>
          </Button>
        </DialogClose>
      )}
    </div>
  </div>
)
DialogHeader.displayName = "DialogHeader"

/**
 * Shared dialog action bar. Keep this outside the padded/scrolling body.
 * Put editor controls in `toolbar`; keep Cancel and the primary action as children.
 * Reserved for inventoried custom editor layouts; ordinary dialogs use named components.
 * Custom editors, task creation, and wizards retain their existing layout.
 */
const DialogFooter = ({
  className,
  toolbar,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { toolbar?: React.ReactNode }) => (
  <>
    {toolbar && <div className="bg-background shrink-0 border-t px-3 py-2">{toolbar}</div>}
    <div className={cn("bg-tinted text-tinted-foreground flex items-center border-t p-2", className)} {...props} />
  </>
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  const entryId = useDialogEntryId()
  return (
    <DialogPrimitive.Title
      ref={ref}
      {...(entryId ? { id: `app-dialog-title-${entryId}` } : {})}
      className={cn("leading-none font-medium tracking-tight", className)}
      {...props}
    />
  )
})
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-muted-foreground text-sm", className)} {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
